// Revenue P0 (P0-1) — checkout/auth continuity. The paywall replaces the whole page with the LemonSqueezy hosted
// checkout (window.location = url), which kills any in-memory React state (a mid-guided-authoring draft, the active
// view, etc). Before that redirect, PaywallModal saves a small, VERSIONED, EXPIRING snapshot here so the client
// `/checkout/success` route can restore the user to where they were once the webhook fulfills. Cleared on restore
// (or on expiry) — this is a one-shot breadcrumb, not persistent state.
const KEY = 'cvcraft_checkout_ctx';
const TTL_MS = 30 * 60 * 1000; // 30 minutes — comfortably longer than a checkout should ever take

export interface CheckoutContext {
  v: 1;
  exp: number;          // ms epoch — load() treats an expired snapshot as absent
  view: string;          // the App view to restore into ('guided-author' | 'review' | 'evaluate-read' | 'author' | …)
  cvId: string;           // the CV being unlocked (also carried by the webhook/redeem flow)
  // WHICH tier the buyer just purchased — the poll's own INTENT. A 'single' purchase pays for THIS cv directly, so
  // the return-trip poll must NOT auto-redeem a bundle credit against it (that spends ~CA$16 of credit on a CV the
  // ~CA$19 order already covers, and the single's fulfillment then silently no-ops on `paid_at IS NULL`). OPTIONAL
  // on purpose: contexts stored before this field existed load fine and keep the pre-existing auto-redeem behavior,
  // so an in-flight buyer is never stranded mid-checkout by the deploy.
  tier?: 'single' | 'cycle';
  guidedMode?: 'author' | 'refine'; // GuidedAuthor's mode, when view === 'guided-author'
  pendingSaveSnapshot?: unknown;    // reserved — GuidedAuthor persists its OWN pending-save under a separate key
}

/** Save the checkout context before the LS redirect. Best-effort: a localStorage failure (private mode, quota)
 *  never blocks checkout — it just means the return trip won't auto-restore (the manual-recovery panel still works). */
export function save(ctx: Omit<CheckoutContext, 'v' | 'exp'>): void {
  try {
    const full: CheckoutContext = { v: 1, exp: Date.now() + TTL_MS, ...ctx };
    window.localStorage.setItem(KEY, JSON.stringify(full));
  } catch {
    // best-effort — swallow (private browsing / quota)
  }
}

/** Load the saved context, or null if absent, unparseable, wrong version, or expired. Does NOT clear it — call
 *  clear() explicitly once the caller has actually consumed/restored it. */
export function load(): CheckoutContext | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CheckoutContext>;
    if (parsed.v !== 1 || typeof parsed.exp !== 'number' || typeof parsed.view !== 'string' || typeof parsed.cvId !== 'string') return null;
    if (Date.now() > parsed.exp) return null;
    return parsed as CheckoutContext;
  } catch {
    return null;
  }
}

/** Clear the saved context (after a successful restore, or when the user abandons the return flow). */
export function clear(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // best-effort
  }
}
